import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import RevealText from "../components/RevealText";
import Marquee from "../components/Marquee";
import MagneticButton from "../components/MagneticButton";
import MediaImage from "../components/MediaImage";
import MediaVideo from "../components/MediaVideo";
import { cldVideoUrl } from "../lib/cloudinary";
import { projects, services, clients, showreel, getProjectVideoIds } from "../lib/data";
import { useFeaturedProjects, useProjects, useTestimonials } from "../hooks/useProjects";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 240]);
  const scaleHeading = useTransform(scrollYProgress, [0, 1], [1, 1.06]);

  const { data: allProjects = projects } = useProjects();
  const { data: selectedWork = [] } = useFeaturedProjects();
  const { data: testimonials = [] } = useTestimonials();

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section ref={heroRef} className="relative isolate min-h-screen bg-white pt-[calc(var(--site-header-offset)+1.75rem)] md:pt-36 px-6 md:px-12 pb-16 overflow-hidden">
        <div className="absolute top-[var(--site-header-offset)] bottom-0 left-0 right-0 md:inset-0 z-0 pointer-events-none">
          <motion.div className="absolute inset-0" style={{ y: yImg }}>
            <video
              className="hidden md:block absolute inset-0 w-full h-full object-cover"
              src={cldVideoUrl(showreel.heroVideos.web)}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              aria-hidden
            />
            <video
              className="md:hidden absolute inset-0 w-full h-full object-cover"
              src={cldVideoUrl(showreel.heroVideos.mobile)}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              aria-hidden
            />
          </motion.div>
        </div>

        <div className="relative z-10">
        <div className="flex items-center justify-between font-body uppercase text-xs tracking-[0.2em] text-black/60">
          <span>Jodhpur — India</span>
          <span className="hidden md:inline">Est. 2024 · {new Date().getFullYear()}</span>
          <span>Creative Studio</span>
        </div>

        <motion.h1
          style={{ scale: scaleHeading }}
          className="mt-8 md:mt-12 font-display uppercase leading-[0.84] tracking-mega text-[14vw] md:text-[9vw] lg:text-[8vw]"
        >
          <span className="block"><RevealText>Every great</RevealText></span>
          <span className="block outline-text"><RevealText delay={0.08}>brand</RevealText></span>
          <span className="block"><RevealText delay={0.16}>starts with</RevealText></span>
          <span className="block">
            <RevealText delay={0.24}>
              an <span className="text-[#F42B29]">idea.</span>
            </RevealText>
          </span>
        </motion.h1>

        <div className="mt-14 flex items-center gap-3 font-body uppercase text-[10px] tracking-[0.3em] text-black/40">
          <span className="inline-block w-10 h-px bg-black/40" />
          <span>Scroll to enter</span>
        </div>
        </div>
      </section>

      {/* HERO INTRO */}
      <section className="px-6 md:px-12 pt-10 pb-10 md:pt-12 md:pb-8 bg-white" data-testid="home-hero-intro">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 md:gap-10">
          <p className="max-w-lg font-body text-lg md:text-xl text-black/70 leading-relaxed">
            Sauce Studios is your creative partner.
            <br />
            Your partner in crime, creativity and everything in between.
            <br />
            We think with you, create alongside you, and care about your brand as if it were our own
          </p>
          <div className="flex items-center gap-4">
            <MagneticButton
              as={Link}
              to="/work"
              className="group inline-flex items-center gap-3 bg-black text-white px-7 py-5 font-body uppercase text-sm tracking-[0.2em] hover:bg-[#F42B29] transition-colors"
              data-testid="hero-view-work-btn"
            >
              See the work <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45 duration-500" />
            </MagneticButton>
            <MagneticButton
              as={Link}
              to="/contact"
              className="inline-flex items-center gap-3 border border-black/20 px-7 py-5 font-body uppercase text-sm tracking-[0.2em] hover:border-black"
              data-testid="hero-contact-btn"
            >
              Say hello
            </MagneticButton>
          </div>
        </div>
      </section>

      {/* SELECTED WORK */}
      <section className="px-6 md:px-12 pt-8 md:pt-6 pb-8 md:pb-12" data-testid="home-selected-work">
        <div className="mb-12 md:mb-20 grid grid-cols-[1fr_auto] gap-x-6 gap-y-3 md:items-end">
          <p className="col-start-1 row-start-1 self-center font-body uppercase text-xs tracking-[0.2em] text-black/50">
            001 — Selected Work
          </p>
          <Link
            to="/work"
            className="link-line col-start-2 row-start-1 self-center md:row-start-2 md:self-end font-body uppercase text-xs md:text-sm tracking-[0.2em] shrink-0 whitespace-nowrap md:pb-1"
            data-testid="home-view-all-link"
          >
            View all ({allProjects.length})
          </Link>
          <h2 className="col-span-2 md:col-span-1 col-start-1 row-start-2 font-display text-5xl md:text-8xl uppercase tracking-tight min-w-0">
            <RevealText>Recent <em className="font-editorial italic">obsessions</em></RevealText>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {selectedWork.map((p, i) => {
            const videoIds = getProjectVideoIds(p);
            return (
              <Link
                key={p.slug}
                to={`/work/${p.slug}`}
                className="work-tile group relative aspect-[4/5] block bg-black/5"
                data-testid={`home-work-tile-${p.slug}`}
                data-cursor="view"
              >
                <MediaImage publicId={videoIds[0]} cover={p.cover} videoIds={videoIds} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 mix-blend-multiply opacity-0 group-hover:opacity-80 transition-opacity duration-500" style={{ backgroundColor: p.color }} />
                <div className="absolute inset-0 p-6 md:p-10 flex flex-col justify-between text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="flex items-start justify-between font-body uppercase text-xs tracking-[0.2em]">
                    <span>{p.industry} · {p.client}</span>
                  </div>
                  <div>
                    <h3 className="font-display text-4xl md:text-7xl uppercase tracking-mega leading-[0.9]">{p.title}</h3>
                    <div className="mt-4 flex items-center gap-3 text-xs uppercase tracking-[0.2em]">
                      <span>View project</span>
                      <ArrowUpRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
                <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between text-black font-body uppercase text-[10px] tracking-[0.2em] group-hover:opacity-0 transition-opacity">
                  <span>{(i + 1).toString().padStart(2, "0")} / {p.title}</span>
                  <span>{p.industry}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* SHOWREEL */}
      <section className="relative px-6 md:px-12 pt-8 md:pt-12 pb-8 md:pb-12 bg-black text-white overflow-hidden" data-testid="home-showreel">
        <div className="flex items-center justify-between mb-3 md:mb-16">
          <p className="font-body uppercase text-xs tracking-[0.2em] text-white/50">002 — Showreel</p>
          <p className="font-body uppercase text-xs tracking-[0.2em] text-white/50 shrink-0">Click to play</p>
        </div>
        <MediaVideo publicId={showreel.video} cover={showreel.cover} title={showreel.title} aspect="aspect-[16/9]" playButtonWhite />
        <p className="mt-6 max-w-md font-editorial italic text-lg md:text-2xl text-white/80">{showreel.caption}</p>
      </section>

      {/* SERVICES */}
      <section className="px-6 md:px-12 pt-8 md:pt-12 pb-24 md:pb-40" data-testid="home-services">
        <div className="grid grid-cols-12 gap-x-6 gap-y-6 mb-16">
          <p className="col-span-12 font-body uppercase text-xs tracking-[0.2em] text-black/50">003 — Capabilities</p>
          <h2 className="col-span-12 md:col-span-5 font-display text-5xl md:text-7xl uppercase tracking-mega"><RevealText>What we</RevealText><br/><RevealText delay={0.08}><em className="font-editorial italic">cook.</em></RevealText></h2>
          <p className="col-span-12 md:col-span-6 md:col-start-7 md:self-center font-body text-lg md:text-xl text-black/70 max-w-lg">
            Ten ways we help brands say the unsaid, frame the unframed, and walk away with something they didn&apos;t know they needed.
          </p>
        </div>

        <ul className="border-t border-black/10">
          {services.map((s) => (
            <li
              key={s.n}
              className="group border-b border-black/10 py-6 md:py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-6 transition-colors hover:bg-black hover:text-white cursor-default px-2 md:px-4"
              data-testid={`service-${s.n}`}
            >
              <div className="flex items-center gap-4 md:gap-6 min-w-0 flex-1">
                <span className="font-body text-xs uppercase tracking-[0.2em] opacity-60 w-12 shrink-0">{s.n}</span>
                <h3 className="font-display text-3xl md:text-6xl uppercase tracking-mega flex-1 transition-transform duration-500 group-hover:translate-x-4">{s.name}</h3>
              </div>
              <p className="font-body text-sm max-w-xs md:text-right opacity-70 pl-16 md:pl-0">{s.description}</p>
              <ArrowUpRight className="hidden md:block w-6 h-6 md:w-8 md:h-8 opacity-0 group-hover:opacity-100 transition-opacity duration-300 shrink-0" />
            </li>
          ))}
        </ul>
      </section>

      {/* WHY SAUCE */}
      <section className="px-6 md:px-12 py-16 md:py-24 2xl:py-40 bg-[#F4B4C4] text-black" data-testid="home-why-sauce">
        <p className="font-body uppercase text-xs tracking-[0.2em] text-black/70 mb-8 md:mb-16">004 — Why Sauce</p>
        <div className="max-w-full space-y-4 md:space-y-[clamp(1rem,2.5vw,3rem)] 2xl:space-y-12 font-display uppercase tracking-mega leading-[1.02] md:leading-[0.94] 2xl:leading-[0.86] text-[clamp(1.75rem,7.5vw,2.5rem)] md:text-[clamp(2.5rem,6.5vw,7.5rem)] 2xl:text-[135px] text-black">
          <p><RevealText>We see <span className="text-[#F42B29]">cinematically.</span></RevealText></p>
          <p className="md:pl-[8vw] 2xl:pl-[20%]"><RevealText delay={0.05}>We ask <span className="text-[#F42B29]">why.</span></RevealText></p>
          <p className="md:pl-[3vw] 2xl:pl-[8%]"><RevealText delay={0.1}>We say <span className="text-[#F42B29]">no.</span></RevealText></p>
          <p className="md:pl-[12vw] 2xl:pl-[28%]"><RevealText delay={0.15}>We choose <span className="text-[#F42B29]">quality.</span></RevealText></p>
          <p className="md:pl-[5vw] 2xl:pl-[12%]"><RevealText delay={0.2}>We build with <span className="text-[#F42B29]">intention.</span></RevealText></p>
        </div>
      </section>

      {/* CLIENTS MARQUEE */}
      <section className="py-16 md:py-24 border-y border-black/10" data-testid="home-clients">
        <p className="px-6 md:px-12 font-body uppercase text-xs tracking-[0.2em] text-black/50 mb-8">005 — Trusted by</p>
        <Marquee items={clients} />
      </section>

      {/* TESTIMONIALS */}
      <section className="px-6 md:px-12 py-16 md:py-40" data-testid="home-testimonials">
        <p className="font-body uppercase text-xs tracking-[0.2em] text-black/50 mb-8 md:mb-12">006 — Kind words</p>
        <div className="grid grid-cols-12 gap-0 md:gap-12">
          {testimonials.map((t, i) => (
            <motion.blockquote
              key={`${t.author}-${i}`}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: i * 0.1 }}
              className={`col-span-12 py-8 border-b border-black/10 last:border-b-0 md:border-b-0 md:py-0 ${i === 1 ? "md:col-span-7 md:col-start-6" : i === 0 ? "md:col-span-8" : "md:col-span-9 md:col-start-4"}`}
              data-testid={`testimonial-${i}`}
            >
              <p className="font-editorial italic text-lg leading-relaxed md:text-3xl lg:text-4xl md:leading-tight">&ldquo;{t.quote}&rdquo;</p>
              <footer className="mt-4 md:mt-6 font-body uppercase text-xs tracking-[0.2em] text-black/60">— {t.author}</footer>
            </motion.blockquote>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 md:px-12 pt-16 pb-6 md:pt-32 md:pb-12 bg-black text-white relative md:overflow-hidden" data-testid="home-final-cta">
        <p className="font-body uppercase text-xs tracking-[0.2em] text-white/50">007 — Let&apos;s talk</p>
        <h2 className="mt-8 font-display uppercase text-[clamp(2.25rem,10.5vw,3.25rem)] md:text-[9vw] leading-[0.9] tracking-mega max-w-full pr-2 md:pr-0">
          <RevealText>Build something</RevealText><br/>
          <RevealText delay={0.08}><em className="font-editorial italic inline-block">unforgettable</em>.</RevealText>
        </h2>
        <div className="mt-8 md:mt-20">
          <MagneticButton
            as={Link}
            to="/contact"
            className="group inline-flex items-center gap-5 bg-[#F42B29] text-white px-10 py-7 hover:bg-[#FF4A01] transition-colors"
            data-testid="home-final-cta-btn"
          >
            <span className="font-body uppercase text-base tracking-[0.2em]">Start a project</span>
            <ArrowUpRight className="w-6 h-6 group-hover:rotate-45 transition-transform duration-500" />
          </MagneticButton>
        </div>
      </section>
    </div>
  );
}
